import {
  HttpErrorResponse,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@shared/services/auth/auth.service';
import { LoaderService } from '@shared/services/loader.service';
import { throwError } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';

/**
 * URLs that should be excluded from authentication and loading indicators
 */
const EXCLUDED_URLS = [
  'assets/i18n',
  '/auth/forget-password',
  '/auth/reset-password',
  '/api/v1/auth/user-info',
];

/**
 * Error messages for different scenarios
 */
const ERROR_MESSAGES = {
  INVALID_USER_DATA: 'Invalid user data. Please login again.',
  ROLE_NOT_FOUND: 'Role not found. Please login again.',
  MISSING_AUTH_DATA: 'Missing authentication or organization data.',
} as const;

export const authInterceptor: HttpInterceptorFn = (req: HttpRequest<any>, next: HttpHandlerFn) => {
  const loadingService = inject(LoaderService);
  const _router = inject(Router);
  const _authService = inject(AuthService);
  const isExcluded =
    req.url.includes('/api/v1/auth/user-info') ||
    (!req.url.includes('/api/v1/') && EXCLUDED_URLS.some((url) => req.url.includes(url)));
  // Start loading for non-excluded requests
  loadingService.startLoading();
  if (!isExcluded) {
    const userVerificationData = getUserTokenData();
    if (!userVerificationData.success) {
      loadingService.stopLoading();
      return throwError(() =>
        createHttpError(403, userVerificationData.error || 'Authentication error')
      );
    }

    const clonedReq = req.clone({
      setHeaders: {
        Authorization: (userVerificationData.data.token as string).includes('Bearer')
          ? userVerificationData.data.token
          : `Bearer ${userVerificationData.data.token}`,
        'x-organization-id': userVerificationData.data.organizationId,
      },
    });

    return next(clonedReq).pipe(
      finalize(() => loadingService.stopLoading()),
      catchError((e) => {
        if (e.status === 401) {
          _authService.setLocalRedirectUrl(_router.url);
          _authService.sendToAuth();
        }
        return throwError(() => e);
      })
    );
  }

  return next(req).pipe(
    finalize(() => loadingService.stopLoading()),
    catchError((e) => {
      confirm(`Error: ${e.message || 'An error occurred while processing your request.'}`);
      return throwError(() => e);
    })
  );
};

/**
 * Extracts and validates user token data from localStorage
 */
function getUserTokenData(): { success: boolean; data?: any; error?: string } {
  const _authService = inject(AuthService);
  const currentUserInfo = _authService.getLoggedInUserInfo();
  if (!currentUserInfo) {
    return { success: false, error: ERROR_MESSAGES.MISSING_AUTH_DATA };
  }

  try {
    const roleId =
      typeof currentUserInfo?.tUser?.tRole === 'string'
        ? currentUserInfo.tUser.tRole
        : currentUserInfo?.tUser?.tRole?._id;

    const token = currentUserInfo?.sIdToken;

    const organizationId =
      typeof currentUserInfo?.tUser?.tUserDetails?.tOrganization === 'string'
        ? currentUserInfo.tUser.tUserDetails.tOrganization
        : currentUserInfo?.tUser?.tUserDetails?.tOrganization?._id;

    if (!roleId) {
      return { success: false, error: ERROR_MESSAGES.ROLE_NOT_FOUND };
    }

    return {
      success: true,
      data: { roleId, token, organizationId },
    };
  } catch (error) {
    return { success: false, error: ERROR_MESSAGES.INVALID_USER_DATA };
  }
}

/**
 * Creates a standardized HTTP error response
 */
function createHttpError(status: number, message: string): HttpErrorResponse {
  return new HttpErrorResponse({
    status,
    statusText: status === 403 ? 'Forbidden' : 'Error',
    error: { message },
  });
}
